import type { PayloadAction } from '@reduxjs/toolkit';
import type { TAvailableLanguageCodes, TQuestionDisplayMode } from '@hubertai/screening-question-types';
import type { QuestionEditorsState } from './Question.Editor.Slice';
import { ECreateQuestionMode } from 'components/Questionsv2/builder/context/create-question.context';
import { DEFAULT_SCHEDULING_CONFIG } from 'types/questions/scheduling.types';
import type { TSchedulingConfig } from 'types/questions/scheduling.types';

interface QuestionGeneralOtherState {
  mode: ECreateQuestionMode;
  isReadOnly: boolean;
  isPublishedOrNonExclusiveQuestion: boolean;
  previewRefreshKey: number;
  scoring_category?: string;
  weight?: number;
  pre_actions: string[];
  post_actions: string[];
}

const initialState: QuestionGeneralOtherState = {
  mode: ECreateQuestionMode.Create,
  isReadOnly: true,
  isPublishedOrNonExclusiveQuestion: false,
  previewRefreshKey: 0,
  pre_actions: [],
  post_actions: [],
};

export const questionGeneralOtherSliceReducer = {
  resetGeneralOther: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const { questionId } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId] = {
        ...state.byId[questionId],
        ...initialState,
      };
    }
  },

  setIsReadOnly: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; value: boolean }>) => {
    const { questionId, value } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId].isReadOnly = value;
    }
  },

  setRefreshPreview: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const { questionId } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId].previewRefreshKey = state.byId[questionId].previewRefreshKey + 1;
      state.byId[questionId].stateModified = true;
    }
  },

  updateDefaultScoringCategory: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; category: string }>,
  ) => {
    const { questionId, category } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId].scoring_category = category;
      state.byId[questionId].stateModified = true;
    }
  },

  updateDefaultWeight: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; weight: number }>) => {
    const { questionId, weight } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId].weight = weight;
      state.byId[questionId].stateModified = true;
    }
  },

  updatePreActions: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; actions: string[] }>) => {
    const { questionId, actions } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId].pre_actions = [...actions];
      state.byId[questionId].stateModified = true;
    }
  },

  updatePostActions: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; actions: string[] }>,
  ) => {
    const { questionId, actions } = action.payload;
    if (state.byId[questionId]) {
      state.byId[questionId].post_actions = [...actions];
      state.byId[questionId].stateModified = true;
    }
  },

  updateHelpText: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      language: TAvailableLanguageCodes;
      value: string;
    }>,
  ) => {
    const { questionId, language, value } = action.payload;
    const question = state.byId[questionId];
    if (!question) return;
    if (!question.help_text) question.help_text = {};
    question.help_text[language] = value;
    question.stateModified = true;
  },

  updateSendDataToLooker: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: boolean }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].send_data_to_looker = value;
    state.byId[questionId].stateModified = true;
  },

  updateRandomizeOrder: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: boolean }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].randomize_order = value;
    state.byId[questionId].stateModified = true;
  },

  updateMinRequired: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: number | undefined }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].min_required = value;
    state.byId[questionId].stateModified = true;
  },

  updateRankingMode: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: 'dnd' | 'click' }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].ranking_mode = value;
    state.byId[questionId].stateModified = true;
  },

  updateDisplayMode: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: TQuestionDisplayMode }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].display_mode = value;
    state.byId[questionId].stateModified = true;
  },

  updateTitle: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      language: TAvailableLanguageCodes;
      value: string;
    }>,
  ) => {
    const { questionId, language, value } = action.payload;
    const question = state.byId[questionId];
    if (!question) return;
    if (!question.title) question.title = {};
    question.title[language] = value;
    question.stateModified = true;
  },

  // Audio / video (recording) question settings
  updateMaxDuration: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: number | undefined }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].max_duration_seconds = value;
    state.byId[questionId].stateModified = true;
  },

  updateMinDuration: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: number | undefined }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].min_duration_seconds = value;
    state.byId[questionId].stateModified = true;
  },

  updateAllowRetake: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; value: boolean }>) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].allow_retake = value;
    // max_retakes overrides allow_retake, so a stale count must not keep retakes alive once they are switched off.
    if (!value) state.byId[questionId].max_retakes = undefined;
    state.byId[questionId].stateModified = true;
  },

  updateMaxRetakes: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: number | undefined }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].max_retakes = value;
    state.byId[questionId].stateModified = true;
  },

  updateMaxThinkingSeconds: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: number | undefined }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].max_thinking_seconds = value;
    state.byId[questionId].stateModified = true;
  },
  updateRecordingRequired: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: boolean }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].required = value;
    state.byId[questionId].stateModified = true;
  },

  updateRecordingQuality: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; value: '360p' | '480p' | '720p' }>,
  ) => {
    const { questionId, value } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].recording_quality = value;
    state.byId[questionId].stateModified = true;
  },

  // Scheduling (cal.com) question settings
  updateSchedulingConfig: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; patch: Partial<TSchedulingConfig> }>,
  ) => {
    const { questionId, patch } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].scheduling = {
      ...(state.byId[questionId].scheduling ?? DEFAULT_SCHEDULING_CONFIG),
      ...patch,
    };
    state.byId[questionId].stateModified = true;
  },

  // Scheduling (cal.com): booking-confirmed reply line(s) — whole array per language
  updateSchedulingReplies: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; language: string; replies: string[] }>,
  ) => {
    const { questionId, language, replies } = action.payload;
    if (!state.byId[questionId]) return;
    state.byId[questionId].replies = {
      ...(state.byId[questionId].replies ?? {}),
      [language]: replies,
    };
    state.byId[questionId].stateModified = true;
  },
};
